import React, { useState } from 'react'
import moment from 'moment'
import API from '../../../../services/API'
import BigModal from '../../../../components/Modals/BigModal'
import AnDateTimePicker from '../../../../components/DateTime/DateTimePicker'
import WitnessPicker from '../../../../components/Medicines/WitnessPicker'
import {
  emptyWitness,
  isWitnessComplete,
  toWitnessPayload,
} from '../../../../helpers/medicineWitness'
import {
  Alert,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

const ROUTE_OPTIONS = [
  'Intravenous',
  'Intraosseous',
  'Intramuscular',
  'Subcutaneous',
  'Oral',
  'Buccal',
  'Sublingual',
  'Intranasal',
  'Rectal',
  'Nebulised',
  'Inhaled',
  'Topical',
]

// Edit or remove a tracked medicine administration while the ePCR is still in
// Draft. Edits create a linked correction record; removal voids the record and
// returns the stock to the bag — the original log is kept either way.
const AmendMedicineAdministration = ({
  mode, // 'edit' | 'remove'
  module,
  epcrID,
  onClose,
  onAmended,
  onRemoved,
}) => {
  const [doseValue, doseUnit] = (module?.Dose ?? '').split(' ')
  const [administeredTime, setAdministeredTime] = useState(
    module?.dateTime ? new Date(module.dateTime) : new Date(),
  )
  const [totalDose, setTotalDose] = useState(doseValue ?? '')
  const [totalDoseType, setTotalDoseType] = useState(doseUnit || 'mg')
  const [route, setRoute] = useState(module?.Route ?? '')
  const [reason, setReason] = useState('')
  const [witness, setWitness] = useState(emptyWitness())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // The module data doesn't say whether the drug is controlled, but a witness
  // is only ever recorded on controlled-drug administrations - so one on the
  // record means removing it needs one too. The server enforces regardless.
  const witnessRequired = !!module?.['Witnessed By']

  const handleEdit = (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    API.post(`/medsmx/administration/${module.medicineLogId}/epcr-correct`, {
      epcrID,
      totalDose,
      totalDoseType,
      administeredTime,
      reason,
    })
      .then((res) => {
        if (res.status === 200) {
          onAmended({
            dateTime: administeredTime,
            Dose: `${totalDose} ${totalDoseType}`,
            Route: route,
            medicineLogId: res.data?._id,
          })
        } else {
          setError(res.data?.error ?? 'Unable to update this administration')
        }
      })
      .catch(() => setError('Unable to update this administration'))
      .finally(() => setSubmitting(false))
  }

  const handleRemove = (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    API.post(`/medsmx/administration/${module.medicineLogId}/epcr-void`, {
      epcrID,
      reason,
      witness: toWitnessPayload(witness),
    })
      .then((res) => {
        if (res.status === 200) {
          onRemoved()
        } else {
          setError(res.data?.error ?? 'Unable to remove this administration')
        }
      })
      .catch(() => setError('Unable to remove this administration'))
      .finally(() => setSubmitting(false))
  }

  return (
    <BigModal open onClose={onClose}>
      {mode === 'edit' ? (
        <Stack component="form" onSubmit={handleEdit} gap={2}>
          <Typography variant="title3">
            Edit Administration — {module?.['Medicine Name']}
          </Typography>
          <Alert severity="info">
            The original administration record is kept and a linked correction
            is created. This is only possible while the ePCR is in draft.
          </Alert>
          <AnDateTimePicker
            date={administeredTime}
            onChange={(newDate) => setAdministeredTime(newDate)}
            label="Administration Time"
            fullWidth
          />
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <TextField
              value={totalDose}
              fullWidth
              onChange={(e) => setTotalDose(e.target.value)}
              type="number"
              label="Total Dose"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Select
                      variant="standard"
                      value={totalDoseType}
                      onChange={(e) => setTotalDoseType(e.target.value)}
                    >
                      {['mg', 'g', 'mcg', 'ml'].map((x) => (
                        <MenuItem key={x} value={x}>
                          {x}
                        </MenuItem>
                      ))}
                    </Select>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Route</InputLabel>
              <Select
                value={route}
                onChange={(e) => setRoute(e.target.value)}
                label="Route"
              >
                {ROUTE_OPTIONS.map((x) => (
                  <MenuItem key={x} value={x}>
                    {x}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            label="Reason for Correction"
            placeholder="e.g. Wrong administration time recorded"
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Button variant="outlined" color="error" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            type="submit"
            disabled={submitting}
          >
            Save Correction
          </Button>
        </Stack>
      ) : (
        <Stack component="form" onSubmit={handleRemove} gap={2}>
          <Typography variant="title3">
            Remove Administration — {module?.['Medicine Name']}
          </Typography>
          <Alert severity="warning">
            Use this only if the medicine was recorded in error and was not
            actually given. The stock will be returned to{' '}
            {module?.['From Bag'] ?? 'the bag'} and the administration record
            will be kept with a voided status for governance.
          </Alert>
          <TextField
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            label="Reason for Removal"
            placeholder="e.g. Added to the wrong patient record"
            required
            fullWidth
          />
          <Stack gap={1}>
            <Typography variant="subtitle2">
              Witnessed by
              {witnessRequired ? ' (required for controlled drugs)' : ''}
            </Typography>
            <WitnessPicker
              value={witness}
              onChange={setWitness}
              size="medium"
              required={witnessRequired}
              helperText={
                witnessRequired
                  ? undefined
                  : 'Only needed when removing a controlled drug administration'
              }
            />
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            type="submit"
            disabled={
              submitting || (witnessRequired && !isWitnessComplete(witness))
            }
          >
            Remove Administration
          </Button>
        </Stack>
      )}
    </BigModal>
  )
}

export default AmendMedicineAdministration
